import type {TechnicalIndicatorData} from '../../../components/charts/LightweightCandlestickChart';
import {MULTI_PERIOD_COLORS} from '../../../components/charts/LightweightCandlestickChart';
import type {
    DcaCondition,
    DcaConditions,
    DcaIndicatorSeries,
    DcaMarketCandle,
    DcaMarketCandlesResult,
} from '../../../types/dca';

/** 后端指标序列 → LightweightCandlestickChart 需要的 indicators / visibleIndicators */
export interface ChartIndicatorPayload {
    indicators: Record<string, TechnicalIndicatorData[]>;
    visibleIndicators: Record<string, boolean>;
}

/** 图表高亮段（命中条件的两线夹层，逐根 top/bottom 值） */
export interface DcaHighlightSegment {
    pane: 'main' | 'rsi' | 'macd';
    color: string;
    points: Array<{ time: number; top: number; bottom: number }>;
}

/** 指标图例项（K线图上方可点击显隐） */
export interface IndicatorLegendItem {
    /** 唯一标识（name_period） */
    key: string;
    /** 与图表 visibleIndicators 对应的 key（周期逗号转下划线） */
    visibleKey: string;
    /** 展示文案（如 EMA(12) / MACD(12,26,9)） */
    label: string;
    /** 与图表曲线一致的色值 */
    color: string;
}

/** 命中段底色（半透明，多条件时依次分配） */
const HIGHLIGHT_COLORS = [
    'rgba(77, 159, 255, 0.28)',
    'rgba(235, 47, 150, 0.28)',
    'rgba(146, 84, 222, 0.28)',
    'rgba(0, 217, 126, 0.28)',
    'rgba(245, 166, 35, 0.28)',
];

const num = (v: unknown): number | undefined => {
    if (null == v) {
        return undefined;
    }
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
};

/**
 * 后端指标序列 → 图表组件指标格式。
 * key 规则与 LightweightCandlestickChart 一致：`{BASE}_{base}_{period}`，周期逗号转下划线。
 */
export const toChartIndicators = (result: DcaMarketCandlesResult | null): ChartIndicatorPayload => {
    const indicators: Record<string, TechnicalIndicatorData[]> = {};
    const visibleIndicators: Record<string, boolean> = {};
    const source = result?.indicators;
    if (null == source) {
        return {indicators, visibleIndicators};
    }
    Object.entries(source).forEach(([name, seriesList]) => {
        const base = name.toUpperCase();
        (seriesList ?? []).forEach((s: DcaIndicatorSeries) => {
            if (null == s) {
                return;
            }
            const periodKey = (s.period ?? '').replace(/,/g, '_');
            const key = `${base}_${base.toLowerCase()}_${periodKey}`;
            const visibleKey = `${base}_${periodKey}`;
            if ('MACD' === base) {
                const points = (s.macdValues ?? [])
                    .filter((p) => null != p && null != p.timestamp)
                    .map((p) => ({
                        time: Number(p.timestamp),
                        diff: num(p.diff),
                        signal: num(p.dea),
                        histogram: num(p.macd),
                    }));
                if (points.length > 0) {
                    indicators[key] = points as TechnicalIndicatorData[];
                    visibleIndicators[visibleKey] = true;
                }
            } else {
                const points = (s.values ?? [])
                    .filter((p) => null != p && null != p.timestamp && null != p.value)
                    .map((p) => ({
                        time: Number(p.timestamp),
                        value: num(p.value),
                    }));
                if (points.length > 0) {
                    indicators[key] = points as TechnicalIndicatorData[];
                    visibleIndicators[visibleKey] = true;
                }
            }
        });
    });
    return {indicators, visibleIndicators};
};

/**
 * 后端指标序列 → 图例项（K线图上方展示标识+周期，可点击显隐）。
 * 颜色与 LightweightCandlestickChart 的分配口径一致：同类型按周期升序取 MULTI_PERIOD_COLORS。
 */
export const buildIndicatorLegend = (
    indicators: Record<string, DcaIndicatorSeries[]> | null | undefined,
): IndicatorLegendItem[] => {
    if (null == indicators) {
        return [];
    }
    const items: IndicatorLegendItem[] = [];
    Object.entries(indicators).forEach(([name, seriesList]) => {
        const base = name.toUpperCase();
        const sorted = [...(seriesList ?? [])]
            .filter((s): s is DcaIndicatorSeries => null != s)
            .sort((a, b) => {
                const pa = parseInt(String(a.period ?? '').split(',')[0], 10);
                const pb = parseInt(String(b.period ?? '').split(',')[0], 10);
                return (Number.isNaN(pa) ? 0 : pa) - (Number.isNaN(pb) ? 0 : pb);
            });
        sorted.forEach((s, idx) => {
            const period = s.period ?? '';
            const visibleKey = `${base}_${period.replace(/,/g, '_')}`;
            const palette = MULTI_PERIOD_COLORS[base];
            const color = 'MACD' === base
                ? '#4d9fff'
                : (palette ? palette[idx % palette.length] : '#999999');
            items.push({
                key: `${base}_${period}`,
                visibleKey,
                label: `${base}(${period})`,
                color,
            });
        });
    });
    return items;
};

/**
 * 条件依赖的指标 visibleKey 列表（无指标的条件返回空数组）
 */
export const requiredIndicatorKeys = (cond: DcaCondition): string[] => {
    switch (cond.type) {
        case 'EMA_CROSS':
            return [`EMA_${cond.fast}`, `EMA_${cond.slow}`];
        case 'SMA_CROSS':
            return [`SMA_${cond.fast}`, `SMA_${cond.slow}`];
        case 'PRICE_CROSS_EMA':
            return [`EMA_${cond.period}`];
        case 'RSI':
            return [`RSI_${cond.period}`];
        case 'MACD_CROSS':
            return ['MACD_12_26_9'];
        default:
            return [];
    }
};

/**
 * 指标名+周期 → 时间戳 → 数值 查找表（用于逐根求值）
 */
type SeriesLookup = Map<number, Record<string, number | undefined>>;

const buildSeriesLookup = (indicators: Record<string, DcaIndicatorSeries[]>): SeriesLookup => {
    const lookup: SeriesLookup = new Map();
    Object.entries(indicators ?? {}).forEach(([name, seriesList]) => {
        (seriesList ?? []).forEach((s) => {
            if (null == s) {
                return;
            }
            const period = s.period ?? '';
            (s.values ?? []).forEach((p) => {
                if (null == p || null == p.timestamp) {
                    return;
                }
                const t = Number(p.timestamp);
                const m = lookup.get(t) ?? {};
                m[`${name}_${period}`] = num(p.value);
                lookup.set(t, m);
            });
            (s.macdValues ?? []).forEach((p) => {
                if (null == p || null == p.timestamp) {
                    return;
                }
                const t = Number(p.timestamp);
                const m = lookup.get(t) ?? {};
                m[`${name}_${period}.diff`] = num(p.diff);
                m[`${name}_${period}.dea`] = num(p.dea);
                lookup.set(t, m);
            });
        });
    });
    return lookup;
};

/** 单根K线的条件求值结果 */
interface BarEval {
    matched: boolean;
    top?: number;
    bottom?: number;
}

/**
 * 逐根求值单条条件（镜像后端 DcaConditionEngine 口径；指标序列与K线已按时间对齐）
 */
const evaluateAt = (
    cond: DcaCondition,
    candle: DcaMarketCandle,
    idx: number,
    candles: DcaMarketCandle[],
    lookup: SeriesLookup,
): BarEval => {
    const t = candle.time;
    const row = lookup.get(t) ?? {};
    const close = num(candle.close);
    const fail = {matched: false};

    switch (cond.type) {
        case 'EMA_CROSS':
        case 'SMA_CROSS': {
            const name = 'EMA_CROSS' === cond.type ? 'EMA' : 'SMA';
            const fast = num(row[`${name}_${cond.fast}`]);
            const slow = num(row[`${name}_${cond.slow}`]);
            if (fast === undefined || slow === undefined) {
                return fail;
            }
            const up = 'UP' === cond.dir;
            return {
                matched: up ? fast > slow : fast < slow,
                top: Math.max(fast, slow),
                bottom: Math.min(fast, slow),
            };
        }
        case 'PRICE_CROSS_EMA': {
            const ema = num(row[`EMA_${cond.period}`]);
            if (ema === undefined || close === undefined) {
                return fail;
            }
            const up = 'UP' === cond.dir;
            return {
                matched: up ? close > ema : close < ema,
                top: Math.max(close, ema),
                bottom: Math.min(close, ema),
            };
        }
        case 'RSI': {
            const rsi = num(row[`RSI_${cond.period}`]);
            const level = num(cond.level);
            if (rsi === undefined || level === undefined) {
                return fail;
            }
            const crossUp = 'CROSS_UP' === cond.mode;
            return {
                matched: crossUp ? rsi > level : rsi < level,
                top: Math.max(rsi, level),
                bottom: Math.min(rsi, level),
            };
        }
        case 'MACD_CROSS': {
            const diff = num(row['MACD_12,26,9.diff']);
            const dea = num(row['MACD_12,26,9.dea']);
            if (diff === undefined || dea === undefined) {
                return fail;
            }
            const up = 'UP' === cond.dir;
            return {
                matched: up ? diff > dea : diff < dea,
                top: Math.max(diff, dea),
                bottom: Math.min(diff, dea),
            };
        }
        case 'PRICE_BELOW': {
            const value = num(cond.value);
            if (value === undefined || close === undefined) {
                return fail;
            }
            const matched = close <= value;
            return matched
                ? {matched: true, top: value, bottom: close}
                : {matched: false};
        }
        case 'DRAWDOWN': {
            const windowMin = cond.windowMin ?? 0;
            const pct = num(cond.pct);
            if (close === undefined || pct === undefined || idx + 1 < windowMin) {
                return fail;
            }
            let windowHigh = 0;
            for (let i = idx - windowMin + 1; i <= idx; i++) {
                const h = num(candles[i]?.high);
                if (h !== undefined && h > windowHigh) {
                    windowHigh = h;
                }
            }
            if (windowHigh <= 0) {
                return fail;
            }
            const drawdown = ((windowHigh - close) / windowHigh) * 100;
            return drawdown >= pct
                ? {matched: true, top: windowHigh, bottom: close}
                : {matched: false};
        }
        case 'LOW_TOUCH': {
            const periods = cond.periods ?? 0;
            if (close === undefined || idx < periods) {
                return fail;
            }
            let lowestLow: number | undefined;
            for (let i = idx - periods; i < idx; i++) {
                const l = num(candles[i]?.low);
                if (l !== undefined && (lowestLow === undefined || l < lowestLow)) {
                    lowestLow = l;
                }
            }
            if (lowestLow === undefined) {
                return fail;
            }
            return close <= lowestLow
                ? {matched: true, top: lowestLow, bottom: close}
                : {matched: false};
        }
        default:
            return fail;
    }
};

const paneOf = (cond: DcaCondition): DcaHighlightSegment['pane'] => {
    if ('RSI' === cond.type) {
        return 'rsi';
    }
    if ('MACD_CROSS' === cond.type) {
        return 'macd';
    }
    return 'main';
};

/**
 * 由条件组合 + 展示K线 + 指标序列，计算命中段高亮（逐条件一条，仅包含 matched 的K线夹层）。
 * hiddenKeys 为被用户隐藏的指标 visibleKey 集合：条件依赖的指标被隐藏时，该条件高亮一并隐藏。
 */
export const buildHighlightSegments = (
    conditions: DcaConditions | null,
    candles: DcaMarketCandle[],
    indicators: Record<string, DcaIndicatorSeries[]> | null | undefined,
    hiddenKeys?: Set<string>,
): DcaHighlightSegment[] => {
    if (null == conditions || 0 === conditions.conditions.length || 0 === candles.length || null == indicators) {
        return [];
    }
    const lookup = buildSeriesLookup(indicators);
    return conditions.conditions.map((cond, ci) => {
        // 条件依赖的指标被隐藏 → 跳过该条件高亮（避免出现无对应指标线的悬浮夹层）
        const required = requiredIndicatorKeys(cond);
        if (null != hiddenKeys && required.some((k) => hiddenKeys.has(k))) {
            return {pane: paneOf(cond), color: HIGHLIGHT_COLORS[ci % HIGHLIGHT_COLORS.length], points: []};
        }
        const points: Array<{ time: number; top: number; bottom: number }> = [];
        candles.forEach((candle, idx) => {
            const ev = evaluateAt(cond, candle, idx, candles, lookup);
            if (ev.matched && ev.top !== undefined && ev.bottom !== undefined) {
                points.push({time: candle.time, top: ev.top, bottom: ev.bottom});
            }
        });
        return {
            pane: paneOf(cond),
            color: HIGHLIGHT_COLORS[ci % HIGHLIGHT_COLORS.length],
            points,
        };
    }).filter((seg) => seg.points.length > 0);
};
